import React from 'react';
import { translateBlockValue } from 'i18n';
import { ProjectionStatuses } from 'business/constants';
import { StyTooltipMenuItem } from 'routes/BudgetModule/components/shared/Status/StyStatus';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { tagFromProposal } from 'routes/BudgetModule/components/BudgetProposal/helpers/eventHelpers';
import { ProjectionT } from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/helpers/types';
import { useProjectionMutation } from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/hooks/projection';
import { getStatusColor } from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/helpers/statusHelpers';
import { ProjectionTooltip } from './ProjectionToolTip';
import StatusSelector from 'components/SharedComponents/StatusSelector/StatusSelector';

export default function ProjectionStatus({
    projection,
    statusField,
    resourceSoftwareId,
    budgetProposal,
    resourceName,
    isDaf = false,
    canEdit = true,
}: {
    projection: Pick<ProjectionT, 'id' | 'budgetProposalId'> &
        Partial<
            Pick<
                ProjectionT,
                'atterrissageStatus' | 'pluriannualProjectionStatus'
            >
        >;
    statusField: 'pluriannualProjectionStatus' | 'atterrissageStatus';
    resourceSoftwareId: string;
    budgetProposal: BudgetProposalT; // only for tracking metadata
    resourceName?: string; // only for tracking metadata
    isDaf?: boolean;
    canEdit?: boolean;
}): React.ReactElement {
    const { id, budgetProposalId } = projection;
    const status = projection[statusField]!;
    const updateProjection = useProjectionMutation({
        budgetProposalId,
        projectionId: id,
        resourceSoftwareId,
        statusField,
    });

    const canEditPluriannualProjections =
        statusField === 'atterrissageStatus' ||
        (statusField === 'pluriannualProjectionStatus' &&
            budgetProposal.canEditPluriannualProjections);

    return (
        <StatusSelector
            variant="outlined"
            defaultOption={{
                label: translateBlockValue(status),
                value: status,
                color: getStatusColor(status),
            }}
            options={Object.values(ProjectionStatuses).map((stat) => {
                return {
                    label: translateBlockValue(stat),
                    value: stat,
                    color: getStatusColor(stat),
                };
            })}
            canEdit={canEdit}
            renderValue={(selectedValue: string) => (
                <ProjectionTooltip
                    disabledByBudgetProposal={!canEditPluriannualProjections}
                    disabledByResource={!canEdit}
                    by={budgetProposal.by}
                    defaultTitle={translateBlockValue(status)}
                >
                    <StyTooltipMenuItem value={selectedValue}>
                        {translateBlockValue(selectedValue)}
                    </StyTooltipMenuItem>
                </ProjectionTooltip>
            )}
            onChange={(value) => {
                const newValue = value.value;
                if (isDaf) {
                    trackEventBudget(
                        statusField === 'atterrissageStatus'
                            ? TRACKABLE_EVENTS.budget.budgetOverview.projections
                                  .atterrissage.statusUpdated
                            : TRACKABLE_EVENTS.budget.budgetOverview.projections
                                  .pluriannual.statusUpdated,
                    );
                } else {
                    trackEventBudget(
                        statusField === 'atterrissageStatus'
                            ? TRACKABLE_EVENTS.budget.budgetProposal.projection
                                  .atterrissage.statusUpdated
                            : TRACKABLE_EVENTS.budget.budgetProposal.projection
                                  .pluriannual.statusUpdated,
                        budgetProposal && resourceName
                            ? {
                                  ...tagFromProposal(budgetProposal),
                                  resource: resourceName,
                              }
                            : {},
                    );
                }
                updateProjection.mutate({ status: newValue });
            }}
        />
    );
}
